import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { Layers } from '@modules/core';
import { useZed } from '@modules/core/src/react/useZed';
import { StandardModule } from '@modules/standard/src/StandardModule';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useScreen } from '@modules/tourdanji/src/react/hooks/useScreen';

import { TourDanjiModule } from '../TourDanjiModule';

import styles from './styles/Loading.module.css';

export type LoadingPanelProps = {
  messages?: string[];
};

export const LoadingPanel = ({ messages }: LoadingPanelProps) => {
  const screenInfo = useScreen();
  const zed = useZed<[BabylonjsModule, StandardModule, TourDanjiModule]>();
  
  const guideTextContents = [
    '3D단지투어는 지형, 건축물, 부동산 데이터를 바탕으로 아파트 단지 및 주변 환경에 관한 정보를 제공하는 서비스입니다.',
    '데이터가 조합되는 과정에서 실제와 차이가 발생할 수 있으니, 이를 감안하여 단순 참고용으로만 사용하시기 바랍니다.',
    '3D단지투어는 개인적 용도로만 이용할 수 있는 서비스로, 별도의 합의 없이 영리적인 목적으로 사용할 수 없습니다.',
  ]

  const loadingTextList = useMemo(() => {
    return messages
      ? messages
      : [
          '단지 정보 확인을 시작합니다.',
          '위성사진을 분석하는 중입니다.',
          '건물 설계도를 분석하는 중입니다.',
          '지형을 올리는 중입니다.',
        ];
  }, [messages]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [shouldFadeOut, setShouldFadeOut] = useState(false);
  const [isStop, setStop] = useState(false);
  const intervalId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    intervalId.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % loadingTextList.length);
    }, 2000);

    return () => clearInterval(intervalId.current);
  }, [loadingTextList.length, intervalId]);

  useEffect(() => {
    if (!zed) return;
    zed.api.observer.onLoadCompleted.addOnce(() => {
      setShouldFadeOut(true);
      clearInterval(intervalId.current);
      setTimeout(() => {
        setStop(true);
      }, 1000);
    });
  }, [zed]);

  if (isStop) {
    return null;
  }

  return (
    <div style={{ zIndex: Layers.UI_MODAL }} className={`${styles.container} ${shouldFadeOut ? styles.fadeOut : ''}`}>
      <span className={`${styles.loadingText} ${styles.textAnimation}`} style={{ 
          whiteSpace: 'pre-line',
          fontSize: 'clamp(16px, 4vw, 24px)',
          wordBreak: 'keep-all',
          wordWrap: 'break-word',
          maxWidth: '100vw',
          textAlign: 'center',
          fontFamily: 'Pretendard, Spoqa Han Sans Neo, sans-serif',
        }}>
        {loadingTextList[currentIndex]}
      </span>
      {screenInfo.screenType && (
        <div className={screenInfo.isInComponent ? styles.guideContainerSmall : styles.guideContainer }>
          <div className={styles.guideBackground}>
            <div className={screenInfo.isInComponent ? styles.guideTextSmall : styles.guideText}>
              {guideTextContents.map((content, index) => (
                <span key={index}>{content}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
